import { useEffect, useMemo, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { usePortfolio } from "@/lib/PortfolioProvider";
import { useHeroRail } from "@/lib/HeroRailProvider";

/**
 * HeroCoverflow — a continuous, tilted "coverflow" carousel of the studio's
 * portfolio work, modelled on the reference hero: cards glide right→left, the
 * card nearest the viewport centre is largest and sharpest, while neighbours
 * angle away, scale down, recede in depth and softly blur. Pure RAF + CSS
 * variables (transform/opacity/filter only); silenced under reduced-motion.
 *
 * Decorative by design (aria-hidden) so it never blocks the centred hero copy.
 */
const SPEED_PX_PER_SEC = 78;   // continuous glide speed
const MAX_ROT = 32;            // degrees at the extreme edge
const CURVE_POW = 1.25;        // falloff exponent for depth/scale/blur

export const HeroCoverflow = () => {
    const reduce = useReducedMotion();
    const { branding } = usePortfolio();
    const { items } = useHeroRail();

    const trackRef = useRef(null);
    const cardsRef = useRef([]);
    const rafRef = useRef(0);
    const xRef = useRef(0);
    const halfRef = useRef(0);
    const lastRef = useRef(0);

    // Prefer the studio's actual portfolio work; fall back to the hero-rail
    // image collection if portfolio is sparse.
    const cards = useMemo(() => {
        const port = (branding || [])
            .filter((p) => p && p.image && p.visible !== false)
            .map((p) => ({ src: p.image, alt: p.title || "Pranto.Gfx project" }));
        if (port.length >= 3) return port;
        const rail = (items || [])
            .filter((i) => i && i.image_url && i.visible !== false)
            .map((i) => ({ src: i.image_url, alt: i.alt || "Pranto.Gfx project" }));
        return rail.length ? rail : port;
    }, [branding, items]);

    // Repeat the base set until it is wide enough, then duplicate once more so
    // the -halfWidth wrap is perfectly seamless.
    const loop = useMemo(() => {
        if (!cards.length) return [];
        let base = cards;
        while (base.length < 8) base = [...base, ...cards];
        return [...base, ...base];
    }, [cards]);

    // Measure one-set width for the seamless wrap (and keep it fresh on resize).
    useEffect(() => {
        const measure = () => {
            if (trackRef.current) halfRef.current = trackRef.current.scrollWidth / 2;
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [loop.length]);

    useEffect(() => {
        if (!loop.length) return undefined;
        if (reduce) {
            // Static composed arc: nudge the row so a card sits centred.
            if (trackRef.current) trackRef.current.style.transform = "translate3d(0,0,0)";
            return undefined;
        }

        let running = true;
        lastRef.current = 0;

        const tick = (ts) => {
            if (!running) return;
            const dt = lastRef.current ? Math.min((ts - lastRef.current) / 1000, 0.05) : 0;
            lastRef.current = ts;

            if (!halfRef.current && trackRef.current) halfRef.current = trackRef.current.scrollWidth / 2;

            let x = xRef.current - SPEED_PX_PER_SEC * dt;
            if (halfRef.current && x <= -halfRef.current) x += halfRef.current;
            xRef.current = x;
            if (trackRef.current) trackRef.current.style.transform = `translate3d(${x}px,0,0)`;

            const vw = window.innerWidth;
            const centerX = vw / 2;
            const range = vw * 0.52;
            for (const el of cardsRef.current) {
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                const cc = rect.left + rect.width / 2;
                let norm = (cc - centerX) / range;
                if (norm > 1) norm = 1;
                if (norm < -1) norm = -1;
                const f = Math.pow(Math.abs(norm), CURVE_POW);
                el.style.setProperty("--cf-rot", `${(-norm * MAX_ROT).toFixed(2)}deg`);
                el.style.setProperty("--cf-scale", (1 - f * 0.24).toFixed(3));
                el.style.setProperty("--cf-z", `${(-f * 170).toFixed(1)}px`);
                el.style.setProperty("--cf-blur", `${(f * 3.2).toFixed(2)}px`);
                el.style.setProperty("--cf-op", (1 - f * 0.5).toFixed(3));
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            running = false;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [loop.length, reduce]);

    if (!loop.length) return null;

    return (
        <div className="pg-coverflow" data-testid="hero-coverflow" aria-hidden="true">
            <div className="pg-coverflow__stage">
                <div className="pg-coverflow__track" ref={trackRef}>
                    {loop.map((card, i) => (
                        <div
                            key={`cf-${i}`}
                            ref={(el) => (cardsRef.current[i] = el)}
                            className="pg-coverflow__card"
                        >
                            <img
                                src={card.src}
                                alt={card.alt}
                                className="pg-coverflow__img"
                                loading={i < 6 ? "eager" : "lazy"}
                                decoding="async"
                                draggable={false}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HeroCoverflow;
